import { DecimalPipe } from '@angular/common';
import { Directive, ElementRef, HostListener, OnInit } from '@angular/core';
import { NgControl } from '@angular/forms';
import { Select } from '@ngxs/store';
import { Observable, map, take } from 'rxjs';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { isMultiCurrencyAccountInfoType } from '@finxone-platform/shared/sys-config-types';
import {
  currenciesNoDp,
  formatInputDecimals,
  handleCurrencyDecimals,
  transformToCurrentCurrency,
} from '../../../../../../libs/shared/utils/src/format-price/transform-to-current-currency.util';
import { AccountState, AccountStateModel } from '../state/account.state';

@Directive({
  selector: '[inputPrice]',
})
export class InputPriceDirective implements OnInit {
  isEleFocus = false;
  private currency = '';
  private digitsInfo = '1.2-2';

  @Select(AccountState.getAccounts)
  private accounts!: Observable<AccountStateModel>;

  constructor(private _decimalPipe: DecimalPipe, private _el: ElementRef, private ngControl: NgControl) {}

  public ngOnInit(): void {
    this.accounts
      .pipe(
        take(1),
        map((accountState) => {
          if (accountState.currentAccountId != '') {
            const currentAccount = accountState.accounts?.find(
              (account) => account.accountId === accountState.currentAccountId,
            );
            if (currentAccount !== undefined && isMultiCurrencyAccountInfoType(currentAccount)) {
              this.currency = accountState.currentMultiCurrencyCode ?? '';
            } else {
              this.currency = currentAccount?.currency ?? '';
            }
            if (currenciesNoDp.includes(this.currency)) {
              this.digitsInfo = '1.0-0'; // No decimal places for currencies in the noDp list
            }
            this.subscribeToControlChanges();
            this.patchValueBeforeInitForm();
          }
        }),
      )
      .subscribe();
  }

  private applyTransformation(): void {
    let map = this.ngControl.value;
    if (typeof map === 'string') {
      map = +this._el.nativeElement.value.replace(/,/g, '');
    }
    if (map) {
      this._el.nativeElement.value = transformToCurrentCurrency(map, this.currency);
    }
  }

  private subscribeToControlChanges(): void {
    // This is if the value changes via the form object, not the user input
    if (this.ngControl?.control) {
      this.ngControl.control.valueChanges.subscribe(() => {
        if (!this.isEleFocus) {
          // changes are coming from outside the input
          this.applyTransformation();
        }
      });
    }
  }

  private patchValueBeforeInitForm(): void {
    if (this.ngControl?.value) {
      this.applyTransformation();
    }
  }

  @HostListener('input', ['$event.target.value'])
  onInput() {
    if (this._el.nativeElement.value != null) {
      this._el.nativeElement.value = handleCurrencyDecimals(this._el.nativeElement.value, this.currency);
    }
  }

  @HostListener('focus')
  onFocus() {
    this.isEleFocus = true;

    if (this._el.nativeElement.value != null) {
      if (typeof this._el.nativeElement.value === 'string') {
        this._el.nativeElement.value = this._el.nativeElement.value.replace(/,/g, '');
      }
      if (typeof this._el.nativeElement.value === 'number') {
        this._el.nativeElement.value = this._decimalPipe.transform(
          this._el.nativeElement.value,
          this.digitsInfo,
        );
      }
    }
  }

  @HostListener('blur')
  onBlur() {
    this.isEleFocus = false;

    if (this._el.nativeElement.value) {
      if (this.ngControl?.control)
        this.ngControl.control.setValue(+this._el.nativeElement.value.replace(/,/g, ''));
      this._el.nativeElement.value = formatInputDecimals(this._el.nativeElement.value, this.currency);
    }
  }
}
